import React, { useEffect, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import { Form } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Customer, useLazyGetCustomersListQuery, useGetUserTypeWiseSearchResultsMutation } from "../../../redux/services/customerService";
import ViewButtonComponent from "../../../components/ViewButtonComponent";
import DeleteButtonComponent from "../../../components/DeleteButtonComponent";
import BreadCrumbs from "../../../components/BreadCrumbs";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import ControlledTextField from "../../../components/ControlledTextField";
import ControlledSelectForFiltering from "../../../components/ContolledSelectForFiltering";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import "./index.css";

// Customer status options for filtering
const CUSTOMER_STATUS = [
  { value: "all", label: "All" },
  { value: "registered", label: "Registered" },
  { value: "anonymous", label: "Anonymous" },
];

export type CustomerSearchData = {
  customerStatus: { value: string; label: string };
  searchCustomer: string;
};

const CustomerList: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [initialList, setInitialList] = useState<Customer[]>([]);
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchTextConfirm, setSearchTextConfirm] = useState(false);
  const [isChangeText, setIsChangeText] = useState(false);
  const [ifReset, setIfReset] = useState(false);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // API hooks
  const [
    triggerGetCustomersList,
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      data: customersListData,
      isLoading: isCustomersLoading,
      error: customersError
    },
  ] = useLazyGetCustomersListQuery();

  const [triggerCustomerSearch] = useGetUserTypeWiseSearchResultsMutation();

  // Form management
  const { control, handleSubmit, watch, setValue } = useForm<CustomerSearchData>({
    defaultValues: {
      customerStatus: { value: "all", label: "All" },
      searchCustomer: "",
    },
  });

  const searchCustomerWatcher = watch("searchCustomer");
  const customerStatusWatcher = watch("customerStatus");

  // Search functionality
  const handleSearch = async () => {
    if (isChangeText === true && searchCustomerWatcher.length >= 4) {
      const searchObj = {
        params: `query=${encodeURIComponent(searchCustomerWatcher)}`,
        filters: "userType:Customer",
      };

      try {
        setIsSearchLoading(true);
        const response: any = await triggerCustomerSearch(searchObj);

        if (response?.data?.hits) {
          const availableCustomers = response.data.hits.filter(
            (item: Customer) => {
              if (customerStatusWatcher?.label === "All") {
                return true;
              } else if (customerStatusWatcher?.label === "Registered") {
                return !item.isAnonymousUser;
              } else {
                return item.isAnonymousUser;
              }
            }
          );
          setCustomerList(availableCustomers);
          setInitialList(response.data.hits);
          setIsChangeText(false);
        } else if (response?.error) {
          console.error("Search error:", response.error);
          toast.error("Search failed. Please try again.");
        } else {
          setCustomerList([]);
        }
      } catch (error) {
        console.error("Search exception:", error);
        toast.error("Something went wrong...! Please try again.");
      } finally {
        setIsSearchLoading(false);
      }
    } else {
      const availableCustomers = (initialList || []).filter(
        (item: Customer) => {
          if (customerStatusWatcher?.label === "All") {
            return true;
          } else if (customerStatusWatcher?.label === "Registered") {
            return !item.isAnonymousUser;
          } else {
            return item.isAnonymousUser;
          }
        }
      );
      setCustomerList(availableCustomers);
    }
  };

  const handleFilterStatusWise = async () => {
    try {
      const response = await triggerGetCustomersList();
      if (response?.data) {
        const filteredData = response.data.filter((item: Customer) => {
          if (customerStatusWatcher?.label === "All") {
            return true;
          } else if (customerStatusWatcher?.label === "Registered") {
            return !item.isAnonymousUser;
          } else {
            return item.isAnonymousUser;
          }
        });
        setCustomerList(filteredData);
        setInitialList(response.data);
        if (searchCustomerWatcher.length) {
          setValue("searchCustomer", "");
        }
      } else if (response?.error) {
        toast.error("Something went wrong...! Please try again.");
      }
      setIsSearchLoading(false);
    } catch (error) {
      toast.error("Something went wrong...! Please try again");
      setIsSearchLoading(false);
    }
  };

  const handleSearchTextConfirm = () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    setSearchTextConfirm(false);

    if (searchCustomerWatcher.length >= 4) {
      typingTimer.current = setTimeout(() => {
        setSearchTextConfirm(true);
      }, 2000);
    }
  };

  useEffect(() => {
    // Cleanup timer on component unmount
    return () => {
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
    };
  }, []);

  // Effects
  useEffect(() => {
    if (searchCustomerWatcher.length < 3 && searchCustomerWatcher.length !== 0) {
      handleFilterStatusWise();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerStatusWatcher]);

  useEffect(() => {
    if (searchCustomerWatcher.length > 3) {
      handleSearchTextConfirm();
    } else if (searchCustomerWatcher.length === 0 && ifReset) {
      handleFilterStatusWise();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCustomerWatcher, customerStatusWatcher]);

  useEffect(() => {
    if (searchTextConfirm) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTextConfirm]);

  useEffect(() => {
    handleFilterStatusWise();
    setIfReset(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchCustomerWatcher) {
      setIsChangeText(true);
      setIfReset(true);
    } else {
      setIsChangeText(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCustomerWatcher]);

  const onSubmit = async (formData: CustomerSearchData) => { };

  const handleViewCustomer = (customer: Customer) => {
    // Navigate to customer details page with view path
    navigate(`/customers/customer-list/${customer.id || customer.documentId}`, {
      state: { customer }
    });
  };


  const handleDeleteCustomer = (customer: Customer) => {
    if (
      window.confirm(`Are you sure you want to delete ${customer.displayName}?`)
    ) {
    }
  };

  // const formatDate = (timestamp: { _seconds: number; _nanoseconds: number }) => {
  //   return new Date(timestamp._seconds * 1000).toLocaleDateString();
  // };

  const formatUserType = (isAnonymous: boolean) => {
    return isAnonymous ? "Anonymous" : "Registered";
  };

  const columns: TableColumn<Customer>[] = [
    {
      name: <h6 className="fw-bold">Customer Name</h6>,
      selector: (row) => row.displayName,
      cell: (row) => (
        <div style={{ fontSize: "14px", fontWeight: "bold" }}>
          {row.displayName}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Email</h6>,
      selector: (row) => row.email,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>{row.email || "N/A"}</div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: <h6 className="fw-bold">Phone Number</h6>,
      selector: (row) => row.phoneNumber,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>{row.phoneNumber}</div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">User Type</h6>,
      cell: (row) => (
        <span className={!row.isAnonymousUser ? "text-success" : "text-warning"}>
          {formatUserType(row.isAnonymousUser)}
        </span>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      cell: (row) => (
        <div style={{ fontSize: "14px" }}>
          {/* {formatDate(row.createdAt)} */}
          {row.createdAt ? new Date(row.createdAt._seconds * 1000).toLocaleDateString() : "N/A"}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <ViewButtonComponent onClick={() => handleViewCustomer(row)} />
          <DeleteButtonComponent
            onClick={() => handleDeleteCustomer(row)}
          />
        </div>
      ),
      sortable: false,
      width: "150px",
    },
  ];

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <div>
        <Form
          className="auth-login-form mt-2"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
            <div className="d-flex gap-2 align-items-center">
              <h4>Customer Management</h4>
            </div>
            <div style={{ minWidth: "300px" }}>
              <ControlledTextField
                label={
                  isSearchLoading
                    ? "Search Customers - Please Wait..."
                    : "Search Customers"
                }
                inputName="searchCustomer"
                inputType="text"
                control={control}
                placeholder="Enter at least 4 characters"
                className="custom-input"
                isDisabled={isCustomersLoading}
              />
            </div>

            <div style={{ minWidth: "300px" }}>
              <ControlledSelectForFiltering
                label="Customer Status"
                inputName="customerStatus"
                control={control}
                optionsList={CUSTOMER_STATUS}
                className="custom-input"
                isDisabled={isCustomersLoading}
              />
            </div>
          </div>

          <Card>
            <CardHeader className="customer-search-card-header">
              <div className="d-flex justify-content-between p-2">
                <h5 className="">Customer List</h5>
              </div>
              <hr />
            </CardHeader>
            <CardBody>
              {isCustomersLoading ? (
                <TableLoadingComponent numColumns={6} />
              ) : customersError ? (
                <div className="text-center text-danger">
                  Error loading customers. Please try again.
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={customerList || []}
                  responsive
                  pagination
                  highlightOnHover
                  pointerOnHover
                  paginationPerPage={10}
                  noDataComponent={<div>No customers found</div>}
                />
              )}
            </CardBody>
          </Card>
        </Form>
      </div>
    </>
  );
};

export default CustomerList;